import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { consultarConfiguracionInicial, ejecutarConfiguracionInicial } from '../nucleo/api/nucleoApi.js';
import { obtenerDesplazamientoLegible, obtenerFechaDeNegocioHoy, obtenerZonaDetectada } from '../nucleo/fecha/fechaLocal.js';
import { useSesion } from '../nucleo/sesion/ProveedorDeSesion.jsx';
import { resolverDestinoPosteriorAlAcceso } from '../nucleo/sesion/destinoPosteriorAlAcceso.js';
import AvisoDeError from '../nucleo/avisos/AvisoDeError.jsx';

/**
 * Asistente de primer arranque (RF-002, RF-003).
 *
 * NO pide zona horaria (D-06) ni tema (D-08): a lo más muestra la zona detectada
 * del dispositivo, en solo lectura. La aplicación no trae ninguna persona usuaria
 * ni contraseña de fábrica: la primera es la que se capture aquí.
 * Aquí tampoco hay barra de navegación: no hay sesión iniciada (RN-044f).
 */
export default function Asistente() {
  const navegar = useNavigate();
  const { establecerSesion } = useSesion();

  const [paso, setPaso] = useState(1);
  const [catalogo, setCatalogo] = useState(null);
  const [error, setError] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [datos, setDatos] = useState({ nombre: '', clave: '', contrasena: '', codigoMoneda: 'MXN' });
  // Sesión recién creada, para que la regla única RN-621 resuelva el destino al cerrar.
  const [sesionCreada, setSesionCreada] = useState(null);

  const zona = obtenerZonaDetectada();

  useEffect(() => {
    consultarConfiguracionInicial().then(setCatalogo).catch(setError);
  }, []);

  function continuarDelPaso1() {
    setError(null);
    if (!datos.nombre.trim()) return setError({ mensaje: 'Escribe tu nombre.' });
    if (!datos.clave.trim()) return setError({ mensaje: 'Escribe una clave de acceso.' });
    if (datos.contrasena.length < 8) return setError({ mensaje: 'La contraseña debe tener al menos 8 caracteres.' });
    return setPaso(2);
  }

  async function sembrarYContinuar() {
    setEnviando(true);
    setError(null);
    try {
      const sesion = await ejecutarConfiguracionInicial({
        nombre: datos.nombre.trim(),
        clave: datos.clave.trim(),
        contrasena: datos.contrasena,
        codigoMonedaPredeterminada: datos.codigoMoneda,
      }, obtenerFechaDeNegocioHoy());

      establecerSesion(sesion);
      setSesionCreada(sesion);
      setPaso(3);
    } catch (fallo) {
      setError(fallo);
      setPaso(1);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="centrado">
      <header className="app" style={{ borderRadius: 12, marginBottom: 10 }}>
        <div className="crece">
          <b>Bienvenida a Gastos Personales</b>
          <small>Configuración inicial · no existe ninguna persona usuaria todavía</small>
        </div>
      </header>

      <AvisoDeError error={error} />

      {paso === 1 && (
        <>
          <div className="card">
            <h3>Paso 1 de 3 · Crea tu usuario</h3>
            <p className="ayuda">
              Esta será la primera persona usuaria del hogar. La aplicación no trae ninguna de
              fábrica: la que escribas aquí es la que existirá.
            </p>

            <label htmlFor="nombre">Tu nombre completo</label>
            <input id="nombre" value={datos.nombre} onChange={(e) => setDatos({ ...datos, nombre: e.target.value })} placeholder="Escribe tu nombre" />

            <label htmlFor="clave">Clave de acceso</label>
            <input id="clave" value={datos.clave} onChange={(e) => setDatos({ ...datos, clave: e.target.value })} placeholder="Con la que iniciarás sesión" />
            <div className="ayuda">Sin acentos ni mayúsculas obligatorias; debe ser única en el hogar.</div>

            <label htmlFor="contrasena">Contraseña</label>
            <input id="contrasena" type="password" value={datos.contrasena} onChange={(e) => setDatos({ ...datos, contrasena: e.target.value })} placeholder="Mínimo 8 caracteres" />

            <label htmlFor="moneda">Moneda predeterminada de captura</label>
            <select id="moneda" value={datos.codigoMoneda} onChange={(e) => setDatos({ ...datos, codigoMoneda: e.target.value })}>
              {(catalogo?.monedas ?? [{ codigoISO: 'MXN', nombre: 'Peso Mexicano' }, { codigoISO: 'USD', nombre: 'Dólar Estadounidense' }])
                .map((moneda) => (
                  <option key={moneda.codigoISO} value={moneda.codigoISO}>
                    {moneda.codigoISO} · {moneda.nombre}
                  </option>
                ))}
            </select>
            <div className="ayuda">Es tu preferencia personal: solo tú la cambias después, desde tu perfil.</div>

            <div className="info">
              <b>Zona detectada del dispositivo:</b> {zona ?? 'no disponible'} ({obtenerDesplazamientoLegible()})<br />
              No se te pide ni se guarda: el día de cada gasto será la fecha local de este
              dispositivo.
            </div>
          </div>

          <button className="btn ancho" onClick={continuarDelPaso1}>Continuar</button>
        </>
      )}

      {paso === 2 && (
        <>
          <div className="card">
            <h3>Paso 2 de 3 · Catálogo inicial</h3>
            <p className="ayuda">
              Se preparan los catálogos compartidos del hogar. Todo es editable después desde
              Ajustes.
            </p>

            <div className="fila"><div className="crece"><b>Monedas</b><small>MXN y USD, sin conversión entre ellas</small></div><span className="chip">{catalogo?.monedas?.length ?? 2}</span></div>
            <div className="fila"><div className="crece"><b>Tipos de categoría</b><small>Ingreso y Gasto (catálogo fijo del sistema)</small></div><span className="chip">2</span></div>
            <div className="fila"><div className="crece"><b>Categorías de gasto propuestas</b><small>Editables y reordenables después</small></div><span className="chip">{catalogo?.totalCategoriasGasto ?? 11}</span></div>
            <div className="fila"><div className="crece"><b>Categorías de ingreso propuestas</b><small>Editables y reordenables después</small></div><span className="chip">{catalogo?.totalCategoriasIngreso ?? 5}</span></div>
            <div className="fila"><div className="crece"><b>Calendario de periodos</b><small>Meses naturales, sin apertura ni cierre</small></div><span className="chip">Listo</span></div>
          </div>

          <div className="aviso n">
            Los catálogos son <b>compartidos y de edición abierta</b>: cuando entre la segunda
            persona del hogar podrá editarlos igual que tú.
          </div>

          <button className="btn sec" onClick={() => setPaso(1)}>Atrás</button>
          <button className="btn ancho" onClick={sembrarYContinuar} disabled={enviando}>
            {enviando ? 'Guardando…' : 'Sembrar y continuar'}
          </button>
        </>
      )}

      {paso === 3 && (
        <>
          <div className="card">
            <h3>Paso 3 de 3 · Listo</h3>
            {/* Fila 36 del inventario (rev. 6, D-336, CA-15-14 y CA-15-15). El texto
                anterior decía «los catálogos sembrados. La siembra es idempotente…»:
                infringía RN-627(e) por los dos lados —«sembrados»/«siembra» (ampliación
                D-334) e «idempotente» (ampliación D-336)—. Los dos hechos útiles SE
                CONSERVAN en lenguaje de producto (D-212, mismo criterio que las filas 31
                y 34): ya tiene sus categorías listas y volver a entrar no duplica nada.
                Literal de §4, sin improvisar (RN-629). El recuadro no se mueve. */}
            <p className="ayuda" data-prueba="asistente-paso3-listo">
              Tu usuario quedó creado y tus categorías ya están listas. Volver a abrir la
              aplicación no duplica nada.
            </p>
            <div className="fila"><div className="crece"><b>Persona usuaria</b></div><span>{datos.nombre}</span></div>
            <div className="fila"><div className="crece"><b>Clave de acceso</b></div><span>{datos.clave}</span></div>
            <div className="fila"><div className="crece"><b>Moneda predeterminada</b></div><span>{datos.codigoMoneda}</span></div>
            <div className="fila"><div className="crece"><b>Zona horaria</b></div><span className="ayuda">Detectada del dispositivo</span></div>
            <div className="fila"><div className="crece"><b>Tema</b></div><span className="ayuda">Automático (sigue al sistema)</span></div>
          </div>

          <div className="aviso i">
            {/* [RENOMBRADO 2026-10-10 · incremento 16/17 · RN-647, D-347] */}
            ¿La segunda persona del hogar? Se da de alta después, desde <b>Ajustes › Personas del
            hogar</b>. Tampoco se le pregunta zona horaria.
          </div>

          {/* Al cerrar el primer arranque la persona llega al Tablero (D-319, RF-219).
              El destino no se decide aquí: lo resuelve la regla única RN-621, la misma
              que usa la pantalla de acceso. */}
          <button className="btn ancho" onClick={() => navegar(resolverDestinoPosteriorAlAcceso(sesionCreada), { replace: true })}>
            Entrar a la aplicación
          </button>
        </>
      )}
    </div>
  );
}
